"use client";

import {
  Navbar as HeroUINavbar,
  NavbarContent,
  NavbarMenuToggle,
  NavbarBrand,
  NavbarItem,
} from "@heroui/navbar";
import { Link } from "@heroui/link";
import {
  Button,
  Chip,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@heroui/react";
import { link as linkStyles } from "@heroui/theme";
import NextLink from "next/link";
import clsx from "clsx";
import Image from "next/image"; // Added import for Next.js Image component
import { GearFill, Star } from "react-bootstrap-icons";
import { useState } from "react";

import { siteConfig } from "@/config/site";
import { TwitterIcon, GithubIcon, DiscordIcon } from "@/components/icons";
import { getEnvironment } from "@/config/environments";
import { useAdminAccess } from "@/app/hooks/useAdminAccess";
import { useUserProgress } from "@/app/hooks/useUserProgress";
import { FeedbackModal } from "@/components/FeedbackModal";

export const Navbar = () => {
  const environment = getEnvironment();
  const { hasAdminAccess } = useAdminAccess();
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const hasUserStarted = useUserProgress();

  return (
    <HeroUINavbar
      className="w-full top-0 z-50" // Ensured 'fixed' positioning, full width, top, and z-index
      maxWidth="xl"
      // position="sticky" // Removed sticky prop, as 'fixed' in className handles the floating behavior
    >
      <NavbarContent className="basis-1/5 sm:basis-full" justify="start">
        <NavbarBrand as="li" className="gap-4 max-w-fit">
          <button
            className="flex justify-start items-center gap-1"
            onClick={() => window.location.reload()}
          >
            <Image
              priority // Added priority prop for LCP
              alt="CrowdSec Logo"
              className="w-auto" // Added w-auto to help maintain aspect ratio with CSS
              height={25} // Adjust height as needed
              src="https://app.crowdsec.net/vectors/crowdsec-logo-light.svg"
              width={120} // Adjust width as needed
            />
            {/* <p className="font-bold text-inherit">CrowdSec</p> Removed text logo */}
          </button>
        </NavbarBrand>
        <ul className="hidden lg:flex gap-4 justify-start ml-2">
          {siteConfig.navItems.map((item) => (
            <NavbarItem key={item.href}>
              <NextLink
                className={clsx(
                  linkStyles({ color: "foreground" }),
                  "data-[active=true]:text-primary data-[active=true]:font-medium",
                )}
                color="foreground"
                href={item.href}
                target={item.external ? "_blank" : undefined}
              >
                {item.label}
              </NextLink>
            </NavbarItem>
          ))}
        </ul>
      </NavbarContent>

      <NavbarContent
        className="hidden sm:flex basis-1/5 sm:basis-full"
        justify="end"
      >
        <NavbarItem className="hidden sm:flex gap-2 items-center">
          {/* New Product Flag - Show only after user answered first question */}
          {hasUserStarted && (
            <Button
              className="bg-slate-700/60 hover:bg-slate-600/70 text-gray-300 hover:text-white font-medium px-3 py-1 text-xs shadow-sm border border-slate-600/30 hover:border-slate-500/50 transition-all duration-200"
              radius="md"
              size="sm"
              variant="bordered"
              onPress={() => setIsFeedbackModalOpen(true)}
            >
              <Star className="w-3 h-3 mr-1" />
              Share feedback
            </Button>
          )}

          <Link isExternal aria-label="Twitter" href={siteConfig.links.twitter}>
            <TwitterIcon className="text-default-500" />
          </Link>
          <Link isExternal aria-label="Discord" href={siteConfig.links.discord}>
            <DiscordIcon className="text-default-500" />
          </Link>
          <Link isExternal aria-label="Github" href={siteConfig.links.github}>
            <GithubIcon className="text-default-500" />
          </Link>
          {hasAdminAccess && (
            <Button
              isIconOnly
              as={NextLink}
              className="text-[#f7aa14] hover:opacity-80 transition-colors"
              href="/admin-7k9x2m8q5n1w"
              size="sm"
              target="_blank"
              title={
                process.env.NEXT_PUBLIC_RELEASE_DATE
                  ? `Admin Panel - Released on ${new Date(
                      process.env.NEXT_PUBLIC_RELEASE_DATE,
                    ).toLocaleString()}`
                  : "Admin Panel"
              }
              variant="light"
            >
              <GearFill className="w-5 h-5" />
            </Button>
          )}
          {environment === "local" && (
            <Chip color="success" size="sm" variant="flat">
              LOCAL
            </Chip>
          )}
          {environment === "preview" && (
            <Chip color="primary" size="sm" variant="flat">
              PREVIEW
            </Chip>
          )}
          <Popover showArrow placement="bottom">
            <PopoverTrigger>
              <Chip
                className="cursor-pointer hover:opacity-80 transition-opacity"
                color="warning"
                size="sm"
                variant="flat"
              >
                BETA
              </Chip>
            </PopoverTrigger>
            <PopoverContent className="p-4 max-w-xs">
              <div className="text-center space-y-3">
                <h4 className="font-semibold text-sm">
                  Welcome to CrowdSec Onboarding Beta! 🦙
                </h4>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  You&apos;re using our beta version. Your feedback helps us
                  build better cybersecurity tools for the community.
                </p>
                <Button
                  className="w-full"
                  color="primary"
                  size="sm"
                  onPress={() => setIsFeedbackModalOpen(true)}
                >
                  <Star className="w-3 h-3 mr-1" />
                  Share Your Feedback
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </NavbarItem>
      </NavbarContent>

      <NavbarContent className="sm:hidden basis-1 pl-4" justify="end">
        {/* New Product Flag - Mobile - Show only after user answered first question */}
        {hasUserStarted && (
          <Button
            className="bg-slate-700/60 hover:bg-slate-600/70 text-gray-300 hover:text-white font-medium px-2 py-1 text-xs shadow-sm border border-slate-600/30 hover:border-slate-500/50 transition-all duration-200 mr-2"
            radius="md"
            size="sm"
            variant="bordered"
            onPress={() => setIsFeedbackModalOpen(true)}
          >
            <Star className="w-3 h-3" />
          </Button>
        )}

        <Link isExternal aria-label="Github" href={siteConfig.links.github}>
          <GithubIcon className="text-default-500" />
        </Link>
        {environment === "local" && (
          <Chip color="success" size="sm" variant="flat">
            LOCAL
          </Chip>
        )}
        {environment === "preview" && (
          <Chip color="primary" size="sm" variant="flat">
            PREVIEW
          </Chip>
        )}
        <Popover showArrow placement="bottom">
          <PopoverTrigger>
            <Chip
              className="cursor-pointer hover:opacity-80 transition-opacity"
              color="warning"
              size="sm"
              variant="flat"
            >
              BETA
            </Chip>
          </PopoverTrigger>
          <PopoverContent className="p-4 max-w-xs">
            <div className="text-center space-y-3">
              <h4 className="font-semibold text-sm">
                Welcome to CrowdSec Onboarding Beta! 🦙
              </h4>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                You&apos;re using our beta version. Your feedback helps us build
                better cybersecurity tools for the community.
              </p>
              <Button
                className="w-full"
                color="primary"
                size="sm"
                onPress={() => setIsFeedbackModalOpen(true)}
              >
                <Star className="w-3 h-3 mr-1" />
                Share Your Feedback
              </Button>
            </div>
          </PopoverContent>
        </Popover>
        {hasAdminAccess && (
          <Button
            isIconOnly
            as={NextLink}
            className="text-default-500 hover:text-primary transition-colors"
            href="/admin-7k9x2m8q5n1w"
            size="sm"
            title="Admin Panel"
            variant="light"
          >
            <GearFill className="w-5 h-5" />
          </Button>
        )}
        <NavbarMenuToggle />
      </NavbarContent>

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={isFeedbackModalOpen}
        userJourney={{
          answers: {},
          currentQuestionId: null,
          progressPercent: 0,
        }}
        onCloseAction={() => setIsFeedbackModalOpen(false)}
      />
    </HeroUINavbar>
  );
};
